import {
  AuthenticationResult,
  EventType,
  InteractionStatus,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser'
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate, useMsal } from '@azure/msal-react'
import React, { useEffect, useRef, useState } from 'react'
import { Outlet, Routes, useLocation, useNavigate } from 'react-router'
import { Route, RouterProvider, createBrowserRouter } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import './App.less'
import { withLDProvider } from 'launchdarkly-react-client-sdk'
// @ts-ignore
import Env from './api/gen/Env'
import { warmupMigrationFunction } from './api/hooks/migration'
import { clearSignedOutReason, readSignedOutReason } from './common/auth/signedOutReason'
import { Main } from './routes/Main'
import ApertureOnboarding from './routes/ProjectAperture/Onboarding/Onboarding'
import { CategoryStakeholderResponse } from './routes/SourcingPlan/CategoryStakeholderSurvey'
import { UserUpdateNewEmailConfirmation } from './routes/Users/UserUpdateNewEmailConfirmation'
import * as dt from './ui/design-tokens/tokens'

const authorityHost = Env.entraAuthorityHost || `https://${Env.entraTenantId}.ciamlogin.com`
const authorityHostname = new URL(authorityHost).hostname

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: Env.entraClientId,
    authority: `${authorityHost}/${Env.entraTenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    knownAuthorities: [authorityHostname],
  },
  cache: { cacheLocation: 'localStorage' },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (!containsPii) {
          console.info(`[MSAL ${LogLevel[level]}] ${message}`)
        }
      },
      logLevel: LogLevel.Verbose,
    },
  },
})

// LOGIN_FAILURE fires during MSAL init (handleRedirectPromise) before any React
// component has mounted, so the error is captured here and replayed via subscribers.
let pendingLoginError: string | null = null
const loginErrorListeners = new Set<(err: string | null) => void>()
function setLoginError(err: string | null) {
  pendingLoginError = err
  loginErrorListeners.forEach(fn => fn(err))
}

// After an interrupted redirect (e.g. the user starts logout then hits the browser Back
// button) MSAL leaves its `interaction.status` flag set. The next loginRedirect then throws
// `interaction_in_progress` and the user is trapped on the error screen. Clearing the flag
// from storage lets a fresh page load start a clean interaction.
const INTERACTION_RECOVERY_KEY = 'cx.msalInteractionRecovery'
function clearStaleMsalInteraction() {
  const stores = [window.localStorage, window.sessionStorage]
  stores.forEach(store => {
    Object.keys(store).forEach(key => {
      if (key.includes('interaction.status')) {
        store.removeItem(key)
      }
    })
  })
}
function isInteractionInProgressError(message: string | null): boolean {
  return !!message && /interaction_in_progress|interaction is currently in progress/i.test(message)
}

msalInstance.addEventCallback(event => {
  console.info('[MSAL EVENT]', event.eventType, event)
  if (event.eventType === EventType.LOGIN_SUCCESS) {
    setLoginError(null)
    sessionStorage.removeItem(INTERACTION_RECOVERY_KEY)
    msalInstance.setActiveAccount((event.payload as AuthenticationResult).account)
  } else if (event.eventType === EventType.LOGIN_FAILURE) {
    const e = event.error as any
    setLoginError(e?.errorMessage || e?.message || String(e))
  }
})

const LOGIN_REQUEST = { scopes: ['openid', 'profile', 'email'] }

function usePageViews() {
  const location = useLocation()
  useEffect(() => {
    const rawWindow: any = window
    const _hsq = (rawWindow._hsq = rawWindow._hsq || [])
    if (Env.useHubspot) {
      _hsq.push(['setPath', location.pathname])
      _hsq.push(['trackPageView'])
    } else {
      _hsq.push(['doNotTrack'])
    }
  }, [location])
}

// Restores the pre-login URL and sets onLogin state after redirect — must be inside MsalProvider
function PostLoginNavigator() {
  const { instance } = useMsal()
  const navigate = useNavigate()

  useEffect(() => {
    const id = instance.addEventCallback(event => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const result = event.payload as AuthenticationResult
        navigate(result.state || window.location.pathname, { state: { onLogin: true }, replace: true })
      }
    })
    return () => {
      if (id) {
        instance.removeEventCallback(id)
      }
    }
  }, [instance, navigate])

  return null
}

// Rendered by UnauthenticatedTemplate, which hides only during Startup and HandleRedirect — NOT
// during a sign-out. logoutRedirect clears the account cache and emits LOGOUT_SUCCESS before it
// navigates to Entra, and MsalProvider re-reads accounts on every event, so this mounts while the
// sign-out is still leaving the page. Everything below therefore waits for `idle` (#7665: a
// loginRedirect fired here collided with the logout's interaction flag → "We couldn't sign you in").
export function WarmupAndLogin() {
  const { instance, inProgress } = useMsal()
  const idle = inProgress === InteractionStatus.None
  const triggered = useRef(false)
  const [error, setError] = useState<string | null>(pendingLoginError)
  // #7665 Phase 2 — the idle logout is the only thing that writes this marker. Read in the
  // initializer, clear in an effect: StrictMode runs the initializer twice and keeps the second
  // result, so it must not consume anything. Clearing on mount is what makes the notice show once
  // and a plain reload sign in as usual.
  const [signedOutForInactivity, setSignedOutForInactivity] = useState(() => readSignedOutReason() === 'inactivity')

  // Not during the sign-out itself: that mount would consume the idle logout's marker before the
  // browser had even left, and the page the user actually returns to would find nothing.
  useEffect(() => {
    if (idle) {
      clearSignedOutReason()
    }
  }, [idle])

  useEffect(() => {
    loginErrorListeners.add(setError)
    return () => {
      loginErrorListeners.delete(setError)
    }
  }, [])

  useEffect(() => {
    if (!idle || triggered.current || error || signedOutForInactivity) {
      return
    }
    triggered.current = true
    warmupMigrationFunction().finally(() =>
      instance
        .loginRedirect({
          ...LOGIN_REQUEST,
          state: window.location.pathname + window.location.search,
        })
        // MSAL has already emitted LOGIN_FAILURE, which is what puts the error on screen.
        .catch(() => {}),
    )
  }, [instance, idle, error, signedOutForInactivity])

  // Auto-recover from a stale `interaction_in_progress` (e.g. logout interrupted by Back):
  // clear the flag and reload once. Guarded so a persistent failure falls through to the
  // manual button instead of reload-looping.
  // Held while the inactivity notice is up (#7665): the marker was already cleared on mount, so a
  // reload now would throw the notice away unseen. Dismissing it lets the recovery run.
  // Also held until MSAL is idle: a reload mid-sign-out would cancel its navigation to Entra.
  useEffect(() => {
    if (!idle || signedOutForInactivity) {
      return
    }
    if (isInteractionInProgressError(error) && !sessionStorage.getItem(INTERACTION_RECOVERY_KEY)) {
      sessionStorage.setItem(INTERACTION_RECOVERY_KEY, '1')
      clearStaleMsalInteraction()
      window.location.reload()
    }
  }, [idle, error, signedOutForInactivity])

  // A sign-out is still leaving the page; whatever is shown here would flash and vanish.
  if (!idle) {
    return null
  }

  // Before #7665 the automatic sign-out was silent: someone who stepped away came back to a bare
  // sign-in redirect and no statement of why their session ended.
  if (signedOutForInactivity) {
    return (
      <div style={{ maxWidth: 600, margin: '80px auto', padding: 24, fontFamily: 'sans-serif' }}>
        <h2>You were signed out due to inactivity</h2>
        <p>Your session ended after 30 minutes without activity. Sign in again to pick up where you left off.</p>
        <button onClick={() => setSignedOutForInactivity(false)} style={{ padding: '8px 16px', cursor: 'pointer' }}>
          Sign in again
        </button>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ maxWidth: 600, margin: '80px auto', padding: 24, fontFamily: 'sans-serif' }}>
        <h2>We couldn't sign you in</h2>
        <p style={{ whiteSpace: 'pre-wrap', color: '#a00' }}>{error}</p>
        <button
          onClick={() => {
            // Clear any stale interaction flag and do a full reload so MSAL starts clean.
            clearStaleMsalInteraction()
            window.location.href = window.location.origin + window.location.pathname + window.location.search
          }}
          style={{ padding: '8px 16px', cursor: 'pointer' }}>
          Try again
        </button>
      </div>
    )
  }

  return null
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  return (
    <>
      <UnauthenticatedTemplate>
        <WarmupAndLogin />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
    </>
  )
}

function AppProviders() {
  usePageViews()

  return (
    <MsalProvider instance={msalInstance}>
      <PostLoginNavigator />
      <ThemeProvider theme={dt}>
        <Outlet />
      </ThemeProvider>
    </MsalProvider>
  )
}

function RouterRoot() {
  return (
    <Routes>
      <Route path="/non-login/sourcing-plan/stakeholder-survey" element={<CategoryStakeholderResponse />} />
      <Route path="/non-login/category/stakeholder-survey" element={<CategoryStakeholderResponse />} />
      <Route path="/non-login/user/update-new-email" element={<UserUpdateNewEmailConfirmation />} />
      <Route path="/non-login/aperture/onboarding/*" element={<ApertureOnboarding />} />
      <Route element={<AppProviders />}>
        <Route
          path="/*"
          element={
            <RequireAuth>
              <Main />
            </RequireAuth>
          }
        />
      </Route>
    </Routes>
  )
}

const router = createBrowserRouter([{ path: '*', Component: RouterRoot }])

const App: React.FC<{}> = () => {
  return <RouterProvider router={router} />
}

const AppWithLDProvider = withLDProvider({
  clientSideID: Env.launchDarklyClientId,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(App)

export default AppWithLDProvider
